import React from 'react';
import { tokens } from '@companycam/slab';
import styled from 'styled-components';
import { useIntl, defineMessages } from 'react-intl';

const Label = styled.label`
  display: flex;
  align-items: center;
  font-size: 11px;
  line-height: 1;
  margin: 0;
  color: var(--cc_color_text_subtle);
  font-weight: 600;
  opacity: 0.65;
  cursor: pointer;

  &::before {
    content: '';
    display: flex;
    justify-content: center;
    align-items: center;
    width: 12px;
    height: 12px;
    margin-right: 6px;
    border: 1px solid var(--cc_color_border_input_inactive);
    border-radius: 2px;
    color: ${tokens.color_button_text_secondary_le};
    font-size: 9px;
    line-height: 12px;
    transform: scale(1.2);
  }
`;

const Input = styled.input`
  margin-right: 0;
  display: none;
  appearance: none;
  cursor: pointer;

  &:checked ~ ${Label} {
    opacity: 1;

    ::before {
      content: '✓';
      background: ${tokens.color_button_background_secondary_le};
      border-color: ${tokens.color_button_background_secondary_le};
    }
  }
`;

const TaskIconButton = styled.button`
  position: relative;
  transition: 0.15s ease;
  cursor: pointer;
  color: var(--cc_color_text_default);
  background: none;
  border: 0;
  padding: 0;
  opacity: 0.75;

  &:hover {
    opacity: 1;
  }
`;

const AddPhotosIconButton = styled(TaskIconButton)<{ hide: boolean }>`
  display: ${(props: { hide: boolean }) => (props.hide ? 'none' : 'block')};
`;

const RequirePhotoOption = styled.div`
  align-items: center;
  font-size: 11px;
`;

const TaskOptionsContainer = styled.div<{ isEditState: boolean }>`
  position: absolute;
  top: -16px;
  right: 8px;

  display: flex;
  flex: none;
  justify-content: space-between;
  align-items: center;
  gap: var(--cc_size_spacing_m);

  min-height: 28px;

  padding: var(--cc_size_spacing_xs) var(--cc_size_spacing_s);

  background: var(--cc_color_background_1);
  border-radius: var(--cc_size_border_radius_l);
  box-shadow: 0 var(--cc_size_depth_1_y) var(--cc_size_depth_1_blur)
    var(--cc_color_depth_1);

  @media screen and (min-width: 821px) {
    order: initial;
    opacity: ${(props: { isEditState: boolean }) =>
      props.isEditState ? 1 : 0};
  }
`;

const messages = defineMessages({
  addReferencePhoto: {
    id: 'Projects.taskList.addReferencePhoto',
    defaultMessage: 'Add Reference Images',
    description:
      'Tooltip hover text on the add reference photo icon in the task options',
  },
  taskPhotoRequired: {
    id: 'Projects.taskList.task.taskPhotoRequired',
    defaultMessage: 'Photos Required',
    description: 'label for requiring a task photo',
  },
  addPhotosToTaskTooltip: {
    id: 'Projects.taskList.addPhotosToTask',
    defaultMessage: 'Add Photos',
    description:
      'Tooltip hover text on the add photos icon in the task options',
  },
  taskDelete: {
    id: 'Projects.taskList.task.taskDelete',
    defaultMessage: 'Delete Field',
    description: 'label for deleting a task',
  },
});

type TaskMenuOptionsProps = {
  isVisible: boolean;
  isEditState: boolean;
  photoCaptureRequired: boolean;
  showEditButton: boolean;
  showDeleteButton: boolean;
  showAddTaskPhotoButton: boolean;
  onTogglePhotoRequiredClick: () => void;
  onDeleteClick: () => void;
  onEditClick: () => void;
  onAddPhotosClick: () => void;
  onAddReferencePhotoClick: () => void;
};

const TaskMenuOptions = ({
  isVisible,
  isEditState,
  photoCaptureRequired,
  showEditButton,
  showDeleteButton,
  showAddTaskPhotoButton,
  onTogglePhotoRequiredClick,
  onDeleteClick,
  onEditClick,
  onAddPhotosClick,
  onAddReferencePhotoClick,
}: TaskMenuOptionsProps) => {
  const { formatMessage } = useIntl();

  if (!isVisible) {
    return null;
  }

  if (isEditState) {
    return (
      <TaskOptionsContainer
        id="task-options-container"
        isEditState={isEditState}
      >
        <RequirePhotoOption>
          <Input
            id="photosRequired"
            type="checkbox"
            checked={photoCaptureRequired}
            readOnly
          />
          <Label
            htmlFor="photosRequired"
            className="taskOption"
            onClick={onTogglePhotoRequiredClick}
          >
            {formatMessage(messages.taskPhotoRequired)}
          </Label>
        </RequirePhotoOption>
        <TaskIconButton
          aria-label={formatMessage(messages.addReferencePhoto)}
          className="taskOption hint hint--bottom"
          onClick={onAddReferencePhotoClick}
          type="button"
        >
          <i className="mdi mdi-compare" />
        </TaskIconButton>
        {showDeleteButton ? (
          <TaskIconButton
            aria-label={formatMessage(messages.taskDelete)}
            className="taskOption hint hint--bottom"
            onClick={onDeleteClick}
            type="button"
          >
            <i className="mdi mdi-delete-outline" />
          </TaskIconButton>
        ) : null}
      </TaskOptionsContainer>
    );
  }

  return (
    <TaskOptionsContainer id="task-options-container" isEditState={isEditState}>
      {showEditButton || showDeleteButton ? (
        <TaskIconButton
          aria-label={formatMessage({ id: 'Common.edit' })}
          className="taskOption hint hint--bottom"
          onClick={onEditClick}
          type="button"
        >
          <i className="mdi mdi-pencil-outline" />
        </TaskIconButton>
      ) : null}
      <AddPhotosIconButton
        hide={showAddTaskPhotoButton}
        onClick={onAddPhotosClick}
        className="taskOption hint hint--bottom"
        aria-label={formatMessage(messages.addPhotosToTaskTooltip)}
      >
        <i className="mdi mdi-image-plus-outline" />
      </AddPhotosIconButton>
      {showDeleteButton ? (
        <TaskIconButton
          aria-label={formatMessage(messages.taskDelete)}
          className="taskOption hint hint--bottom"
          onClick={onDeleteClick}
          type="button"
        >
          <i className="mdi mdi-delete-outline" />
        </TaskIconButton>
      ) : null}
    </TaskOptionsContainer>
  );
};

export default TaskMenuOptions;
